import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import CardOne from '@/components/common/card/CardOne'
import ToastMessage from '@/components/common/toast/ToastMessage'
import { isWebView, checkAppPasscode, setAppPasscode, verifyAppPasscode, resetAppPasscode } from '@/utils/webViewBridge'
import { useAppStore } from '@/store/appStore'

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  ariaLabel: string
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, ariaLabel }) => {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`w-10 h-5 rounded-full relative transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
        checked ? 'bg-amber-500' : 'bg-gray-200'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transform transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-5' : ''
        }`}
      ></span>
    </button>
  )
}

interface NumberPadProps {
  onNumberClick: (num: string) => void
  onDelete: () => void
}

const NumberPad: React.FC<NumberPadProps> = ({ onNumberClick, onDelete }) => {
  const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'delete']

  return (
    <div className='grid grid-cols-3 gap-4 max-w-xs mx-auto'>
      {numbers.map((num, index) => {
        if (num === '') {
          return <div key={index} />
        }
        if (num === 'delete') {
          return (
            <button
              key={index}
              onClick={onDelete}
              className='w-16 h-16 rounded-full flex items-center bg-white justify-center text-lg font-medium text-amber-700'
            >
              <ChevronLeft />
            </button>
          )
        }
        return (
          <button
            key={index}
            onClick={() => onNumberClick(num)}
            className='w-16 h-16 rounded-full flex items-center justify-center text-xl font-medium text-amber-800 bg-white'
          >
            {num}
          </button>
        )
      })}
    </div>
  )
}

interface PasswordDotsProps {
  password: string
  maxLength: number
}

const PasswordDots: React.FC<PasswordDotsProps> = ({ password, maxLength }) => {
  return (
    <div className='flex gap-4 justify-center mb-8'>
      {Array.from({ length: maxLength }).map((_, index) => (
        <div
          key={index}
          className={`w-8 h-8 rounded-full border-2 ${
            index < password.length ? 'bg-white border-amber-300' : 'border-white'
          }`}
        />
      ))}
    </div>
  )
}

const PasswordSettingPage = () => {
  const [isChecked, setIsChecked] = useState(false)
  const [isPasswordDetailSetting, setIsPasswordDetailSetting] = useState(false)
  const [passwordStep, setPasswordStep] = useState<'set' | 'confirm' | 'verify'>('set')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verifyPassword, setVerifyPassword] = useState('')
  const [isWebViewEnvironment, setIsWebViewEnvironment] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)
  const navigate = useNavigate()
  const { appPassword, setAppPassword, clearAppPassword } = useAppStore()

  const showToastMessage = (message: string) => {
    setToastMessage(message)
    setShowToast(true)
  }

  useEffect(() => {
    const checkEnvironment = async () => {
      const webViewMode = isWebView()
      setIsWebViewEnvironment(webViewMode)

      if (webViewMode) {
        // 웹뷰 환경에서는 브릿지를 통해 앱 비밀번호 설정 상태 확인
        const passcodeSet = await checkAppPasscode()
        setIsChecked(passcodeSet)
      } else {
        // 웹 환경에서는 기능 사용 불가
        setIsChecked(false)
      }
    }

    checkEnvironment()

    // 앱이 포그라운드로 돌아올 때 상태 재확인
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkEnvironment()
      }
    }

    const handleFocus = () => {
      checkEnvironment()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const handleNumberClick = (num: string) => {
    if (passwordStep === 'set') {
      if (password.length < 4) {
        setPassword(prev => prev + num)
      }
    } else if (passwordStep === 'confirm') {
      if (confirmPassword.length < 4) {
        setConfirmPassword(prev => prev + num)
      }
    } else if (passwordStep === 'verify') {
      if (verifyPassword.length < 4) {
        setVerifyPassword(prev => prev + num)
      }
    }
  }

  const handleDelete = () => {
    if (passwordStep === 'set') {
      setPassword(prev => prev.slice(0, -1))
    } else if (passwordStep === 'confirm') {
      setConfirmPassword(prev => prev.slice(0, -1))
    } else if (passwordStep === 'verify') {
      setVerifyPassword(prev => prev.slice(0, -1))
    }
  }

  const handlePasswordSetComplete = () => {
    if (password.length === 4) {
      setPasswordStep('confirm')
    }
  }

  const handlePasswordConfirmComplete = async () => {
    if (confirmPassword.length === 4) {
      if (password === confirmPassword) {
        // 비밀번호 일치 - 설정 완료
        if (isWebViewEnvironment) {
          // 웹뷰 환경에서는 브릿지를 통해 앱 비밀번호 설정
          const success = await setAppPasscode()
          if (success) {
            setIsPasswordDetailSetting(false)
            setIsChecked(true)
            setPasswordStep('set')
            setPassword('')
            setConfirmPassword('')
            showToastMessage('비밀번호가 설정되었습니다!')
          } else {
            showToastMessage('비밀번호 설정에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          // 웹 환경에서는 Zustand store에 저장
          setAppPassword(password)
          setIsPasswordDetailSetting(false)
          setIsChecked(true)
          setPasswordStep('set')
          setPassword('')
          setConfirmPassword('')
          showToastMessage('비밀번호가 설정되었습니다!')
        }
      } else {
        // 비밀번호 불일치
        showToastMessage('비밀번호가 일치하지 않습니다. 다시 입력해주세요.')
        setConfirmPassword('')
      }
    }
  }

  const handlePasswordVerifyComplete = async () => {
    if (verifyPassword.length === 4) {
      if (isWebViewEnvironment) {
        // 웹뷰 환경에서는 브릿지를 통해 앱 비밀번호 확인 후 해제
        const success = await verifyAppPasscode()
        if (success) {
          const resetSuccess = await resetAppPasscode()
          if (resetSuccess) {
            setIsPasswordDetailSetting(false)
            setIsChecked(false)
            setPasswordStep('set')
            setVerifyPassword('')
            showToastMessage('비밀번호 설정이 해제되었습니다!')
          } else {
            showToastMessage('비밀번호 해제에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          showToastMessage('비밀번호가 일치하지 않습니다. 다시 입력해주세요.')
          setVerifyPassword('')
        }
      } else {
        // 웹 환경에서는 Zustand store에서 확인
        if (verifyPassword === appPassword) {
          // 비밀번호 일치 - 해제 완료
          clearAppPassword()
          setIsPasswordDetailSetting(false)
          setIsChecked(false)
          setPasswordStep('set')
          setVerifyPassword('')
          showToastMessage('비밀번호 설정이 해제되었습니다!')
        } else {
          // 비밀번호 불일치
          showToastMessage('비밀번호가 일치하지 않습니다. 다시 입력해주세요.')
          setVerifyPassword('')
        }
      }
    }
  }

  // 자동으로 다음 단계로 진행 - useEffect로 타이머 관리
  useEffect(() => {
    if (passwordStep === 'set' && password.length === 4) {
      const timerId = setTimeout(handlePasswordSetComplete, 500)
      return () => clearTimeout(timerId)
    }
  }, [passwordStep, password])

  useEffect(() => {
    if (passwordStep === 'confirm' && confirmPassword.length === 4) {
      const timerId = setTimeout(handlePasswordConfirmComplete, 500)
      return () => clearTimeout(timerId)
    }
  }, [passwordStep, confirmPassword])

  useEffect(() => {
    if (passwordStep === 'verify' && verifyPassword.length === 4) {
      const timerId = setTimeout(handlePasswordVerifyComplete, 500)
      return () => clearTimeout(timerId)
    }
  }, [passwordStep, verifyPassword])

  return (
    <div className='min-h-screen'>
      <ToastMessage message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} />
      {!isPasswordDetailSetting ? (
        <>
          <NormalTopBar
            title='비밀번호'
            prevButton
            onPrevButtonClick={() => navigate('/my')}
            textClassName='text-xl text-text-header font-semibold'
            boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
          />
          <div className='p-3 flex flex-col gap-3'>
            <CardOne className='px-3 py-5'>
              <div className='flex justify-between items-center'>
                <p className='font-medium text-sm text-gray-500'>비밀번호 설정</p>
                <ToggleSwitch
                  checked={isChecked}
                  onChange={async () => {
                    // 웹 환경에서는 기능 사용 불가
                    if (!isWebViewEnvironment) {
                      showToastMessage('앱에서만 사용 가능한 기능입니다.')
                      return
                    }

                    if (!isChecked) {
                      // 토글을 켜려고 할 때 (현재 false -> true로 변경)
                      // 웹뷰 환경에서는 바로 네이티브 앱 비밀번호 설정 호출
                      try {
                        console.log('Starting passcode setup...')
                        const success = await setAppPasscode()
                        console.log('Passcode setup result:', success)

                        if (success) {
                          // 설정 성공 후 상태 재확인
                          const currentState = await checkAppPasscode()
                          setIsChecked(currentState)

                          if (currentState) {
                            showToastMessage('비밀번호가 설정되었습니다!')
                          }
                        } else {
                          console.log('Passcode setup failed or cancelled')
                          // 실패 시에도 현재 상태 재확인
                          const currentState = await checkAppPasscode()
                          setIsChecked(currentState)
                        }
                      } catch (error) {
                        console.error('Passcode setup error:', error)
                        // 에러 시에도 현재 상태 재확인
                        const currentState = await checkAppPasscode()
                        setIsChecked(currentState)
                      }
                    } else {
                      // 토글을 끄려고 할 때 - 기존 비밀번호 확인 필요
                      // 웹뷰 환경에서는 바로 네이티브 앱 비밀번호 확인 후 해제
                      try {
                        const success = await verifyAppPasscode()
                        if (success) {
                          const resetSuccess = await resetAppPasscode()
                          if (resetSuccess) {
                            // 해제 성공 후 상태 재확인
                            const currentState = await checkAppPasscode()
                            setIsChecked(currentState)
                            showToastMessage('비밀번호 설정이 해제되었습니다!')
                          } else {
                            showToastMessage('비밀번호 해제에 실패했습니다. 다시 시도해주세요.')
                          }
                        } else {
                          showToastMessage('비밀번호가 일치하지 않습니다.')
                        }
                      } catch (error) {
                        console.error('Passcode reset error:', error)
                        // 에러 시에도 현재 상태 재확인
                        const currentState = await checkAppPasscode()
                        setIsChecked(currentState)
                      }
                    }
                  }}
                  ariaLabel={`비밀번호 설정 ${isChecked ? '켜짐' : '꺼짐'}`}
                />
              </div>
              <hr className='border-amber-200 mt-2 mb-5' />
              {!isWebViewEnvironment && (
                <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                  <p className='text-sm text-amber-800 font-medium'>⚠️ 앱에서만 사용 가능한 기능입니다.</p>
                  <p className='text-xs text-amber-700 mt-1'>비밀번호 설정 기능은 모바일 앱에서만 지원됩니다.</p>
                </div>
              )}
              <p className='text-gray-900'>앱 비밀번호를 설정하면 앱 내에서 비밀번호를 입력하여 사용할 수 있습니다.</p>
            </CardOne>
          </div>
        </>
      ) : (
        <>
          <NormalTopBar
            title={
              passwordStep === 'set' ? '비밀번호 설정' : passwordStep === 'confirm' ? '비밀번호 확인' : '비밀번호 입력'
            }
            prevButton
            onPrevButtonClick={() => {
              if (passwordStep === 'confirm') {
                setPasswordStep('set')
                setConfirmPassword('')
              } else {
                setIsPasswordDetailSetting(false)
                setPasswordStep('set')
                setPassword('')
                setConfirmPassword('')
                setVerifyPassword('')
              }
            }}
            textClassName='text-xl text-text-header font-semibold'
            boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
          />
          <div className='p-6 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] gap-4'>
            <div className='text-center mb-8'>
              <h2 className='text-[32px] font-semibold text-gray-800 mb-2'>
                {passwordStep === 'set'
                  ? '비밀번호 설정'
                  : passwordStep === 'confirm'
                    ? '비밀번호 확인'
                    : '비밀번호 입력'}
              </h2>
              <p className='text-gray-600'>
                {passwordStep === 'set'
                  ? '비밀번호를 입력해주세요.'
                  : passwordStep === 'confirm'
                    ? '비밀번호를 한번 더 입력해주세요.'
                    : '기존 비밀번호를 입력해주세요.'}
              </p>
            </div>

            <PasswordDots
              password={
                passwordStep === 'set' ? password : passwordStep === 'confirm' ? confirmPassword : verifyPassword
              }
              maxLength={4}
            />

            <NumberPad onNumberClick={handleNumberClick} onDelete={handleDelete} />
          </div>
        </>
      )}
    </div>
  )
}

export default PasswordSettingPage
